import React, { useState, useEffect, useRef, ReactNode  } from 'react';
import {Briefcase, GraduationCap, Award, MapPin, ArrowUpRight} from 'lucide-react';
import FilterCheck from '../components/FilterCheck';
import { useLanguage } from '../LanguageContext';
import T from '../components/T';
import { Helmet } from 'react-helmet-async';

type EventType = 'work' | 'education' | 'other';

interface TimelineEvent {
  id: number;
  date: ReactNode;
  title: ReactNode;
  where?: ReactNode;
  url?: string;
  location?: string;
  description: string | ReactNode;
  type: EventType;
}

// One row of the margin-note timeline: a date column plus an entry
// column (icon + title + where, click-to-expand for the description).
interface TimelineRowData {
  date: ReactNode;
  title: ReactNode;
  where?: ReactNode;
  url?: string;
  description: string | ReactNode;
  icon: ReactNode;
  color: string;
}

const TimelineRow: React.FC<{
  data: TimelineRowData;
  isOpen: boolean;
  onToggle: () => void;
  titleRef: (el: HTMLSpanElement | null) => void;
  signatureWidth?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ data, isOpen, onToggle, titleRef, signatureWidth, className = '', style }) => (
  <div className={`flex flex-col md:flex-row md:items-start gap-y-1 md:gap-x-6 mb-8 ${className}`} style={style}>
    {/* The category icon lives in the date margin as its own narrow
        column, so the icons line up top to bottom and every title starts
        flush (picked in /timeline-icon-lab). On mobile, where the row
        stacks, it sits in front of the date instead. */}
    <span className="hidden md:inline-flex flex-shrink-0 -mr-3" style={{ marginTop: '0.15rem' }}>{data.icon}</span>
    <div className="md:w-32 md:flex-shrink-0 md:text-right text-sm text-[var(--color-text-muted)]" style={{fontFamily: 'Short Stack, cursive'}}>
      <span className="inline-flex md:hidden mr-2 align-middle">{data.icon}</span>
      {data.date}
    </div>
    <div className="flex-1 min-w-0">
      <div
        onClick={onToggle}
        data-expanded={isOpen || undefined}
        className="expand-toggle cursor-pointer relative"
      >
        {/* On phones "where" drops under the title instead of sitting
            beside it, where a long place name squeezed the title into a
            narrow column of 3-4 lines. */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] flex items-start flex-1 min-w-0">
            {/* The ref sits on an inner span so the measured width is the
                title text alone, not the expand arrow drawn after it. */}
            <span className="expand-arrow"><span ref={titleRef}>{data.title}</span></span>
          </h3>
          {data.where && (
            <div className="flex items-center text-sm text-[var(--color-text-muted)] mt-0.5 sm:mt-0 sm:ml-4 flex-shrink-0">
              <MapPin size={14} className="mr-1" />
              {/* The organisation links to its website where it has one:
                  muted like the rest of the line, accent on hover, and a
                  small arrow so it reads as a link without shouting. The
                  click must not also toggle the row open/closed. */}
              {data.url ? (
                <a
                  href={data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 hover:text-[var(--color-main-accent)] transition-colors duration-200"
                >
                  {data.where}
                  <ArrowUpRight size={12} aria-hidden="true" />
                </a>
              ) : (
                <span>{data.where}</span>
              )}
            </div>
          )}
        </div>

        {/* No box — bare text + the shared expand-toggle/arrow gesture,
            same as About Me's tiles and CurrentWork's side-projects
            disclosure, not a bordered card. Always-mounted panel
            (grid-rows 0fr->1fr) instead of a conditional render, so it
            animates open/closed instead of popping. */}
        <div className="expand-panel">
          <div className="expand-panel-inner">
            <div>
              {/* Tucked up close under the title (no top margin) so it reads
                  as that title's underline rather than a divider above the
                  description. Draws in on open (width 0 -> the title's
                  width), the same gesture as About Me's tile lines. */}
              <div
                className="h-1 rounded-full mb-3"
                style={{ backgroundColor: data.color, width: isOpen ? (signatureWidth ?? 0) : 0, transition: 'width 0.48s ease-out', transform: isOpen ? 'translateX(5px)' : 'none' }}
              ></div>
              {/* A <div>, not a <p> — some descriptions are themselves
                  rich content (their own <p>/<ul>, e.g. the OneThird
                  entry), which React (rightly) flags as invalid inside a
                  <p>. Plain-string descriptions render the same either
                  way. */}
              <div className="expand-fade text-[var(--color-text-secondary)]">{data.description}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Timeline: React.FC = () => {
  const { t, language }  = useLanguage();
  const [expandedEvents, setExpandedEvents] = useState<number[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<EventType[]>(['work', 'education', 'other']);
  const [showAllEvents, setShowAllEvents] = useState(false);

  const timelineEvents: TimelineEvent[] = [

    {
      id: 32,
      date: <T k="experiencePage.experiences.startupCareer.date" />,
      title: <T k="experiencePage.experiences.startupCareer.title" />,
      where: <T k="experiencePage.experiences.startupCareer.where" />,
      url: 'https://startup.career',
      description: <T k="experiencePage.experiences.startupCareer.description" />,
      type: 'work',
    },
    {
      id: 31,
      date: <T k="experiencePage.experiences.jacobianSystems.date" />,
      title: <T k="experiencePage.experiences.jacobianSystems.title" />,
      where: <T k="experiencePage.experiences.jacobianSystems.where" />,
      description: <T k="experiencePage.experiences.jacobianSystems.description" />,
      type: 'work',
    },
    {
      id: 21,
      date: <T k="experiencePage.experiences.onethirdPm.date" />,
      title: <T k="experiencePage.experiences.onethirdPm.title" />,
      where: <T k="experiencePage.experiences.onethirdPm.where" />,
      url: 'https://onethird.io',
      description: <T k="experiencePage.experiences.onethirdPm.description" />,
      type: 'work',
    },
    {
      id: 2,
      date: <T k="experiencePage.experiences.onethird.date" />,
      title: <T k="experiencePage.experiences.onethird.title" />,
      where: <T k="experiencePage.experiences.onethird.where" />,
      url: 'https://onethird.io',
      description: <T k="experiencePage.experiences.onethird.description" />,
      type: 'work',
    },
    {
      id: 212,
      date: <T k="experiencePage.experiences.hardlist.date" />,
      title: <T k="experiencePage.experiences.hardlist.title" />,
      where: <T k="experiencePage.experiences.hardlist.where" />,
      url: 'https://www.hardli.st',
      description: <T k="experiencePage.experiences.hardlist.description" />,
      type: 'other',
    },
    {
      id: 3,
      date: <T k="experiencePage.experiences.master.date" />,
      title: <T k="experiencePage.experiences.master.title" />,
      where:  <T k="experiencePage.experiences.master.where" />,
      url: 'https://www.rug.nl',
      description: <T k="experiencePage.experiences.master.description" />,
      type: 'education',
    },
    {
      id: 4,
      date:  <T k="experiencePage.experiences.yag.date" />,
      title: <T k="experiencePage.experiences.yag.title" />,
      where: 'Young Advisory Group',
      url: 'https://youngadvisorygroup.nl',
      description: <T k="experiencePage.experiences.yag.description" />,
      type: 'work',
    },
    {
      id: 23,
      date:  <T k="experiencePage.experiences.omun.date" />,
      title: <T k="experiencePage.experiences.omun.title" />,
      where: 'Omun B.V.',
      description: <T k="experiencePage.experiences.omun.description" />,
      type: 'work',
    },
    {
      id: 34,
      date: "2023",
      title: <T k="experiencePage.experiences.kickbox.title" />,
      where: 'Groningen',
      description: <T k="experiencePage.experiences.kickbox.description" />,
      type: 'other',
    },

    {
      id: 5,
      date: <T k="experiencePage.experiences.umcg.date" />,
      title: "Machine Learning Engineer",
      where: <T k="experiencePage.experiences.umcg.where" />,
      url: 'https://www.umcg.nl',
      description: <T k="experiencePage.experiences.umcg.description" />,
      type: 'work',
    },
    {
      id: 6,
      date: "2020-2021",
      title: <T k="experiencePage.experiences.minor.title" />,
      where: <T k="experiencePage.experiences.minor.where" />,
      url: 'https://www.rug.nl',
      description: <T k="experiencePage.experiences.minor.description" />,
      type: 'education',
    },
    {
      id: 7,
      date: <T k="experiencePage.experiences.dialogic.date" />,
      title: <T k="experiencePage.experiences.dialogic.title" />,
      where: 'Dialogic',
      url: 'https://www.dialogic.nl',
      description: <T k="experiencePage.experiences.dialogic.description" />,
      type: 'work',
    },
    {
      id: 999,
      date: <T k="experiencePage.experiences.bg.date" />,
      title: <T k="experiencePage.experiences.bg.title" />,
      where: 'Boot Groningen',
      description:  <T k="experiencePage.experiences.bg.description" />,
      type: 'work',
    },
    {
      id: 8,
      date: <T k="experiencePage.experiences.bachelor.date" />,
      title: <T k="experiencePage.experiences.bachelor.title" />,
      where: <T k="experiencePage.experiences.bachelor.where" />,
      url: 'https://www.rug.nl',
      description: <T k="experiencePage.experiences.bachelor.description" />,
      type: 'education',
    },
    {
      id: 77,
      date: "2021",
      title: <T k="experiencePage.experiences.doordeweeks.title" />,
      where: 'Doordeweeks',
      description: <T k="experiencePage.experiences.doordeweeks.description" />,
      type: 'other',
    },
  ];

  const toggleEvent = (id: number) => {
    setExpandedEvents(prev =>
      prev.includes(id) ? prev.filter(eventId => eventId !== id) : [...prev, id]
    );
  };

  const toggleFilter = (filterType: EventType) => {
    setSelectedFilters(prev =>
      prev.includes(filterType)
        ? prev.filter(f => f !== filterType)
        : [...prev, filterType]
    );
  };

  const filteredEvents = timelineEvents.filter(event => selectedFilters.includes(event.type));
  const displayedEvents = showAllEvents ? filteredEvents : filteredEvents.slice(0, 4);

  // Signature accent line is sized to its title's rendered text width, same
  // technique as Articles.tsx/AboutMe.tsx (a ref on the title +
  // offsetWidth, plus a little padding) — replacing the old fixed 13rem
  // stub per the "always sized to text" design-system rule.
  const titleRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const [signatureWidths, setSignatureWidths] = useState<Record<number, string>>({});

  // Measured for every visible title up front (and again once web fonts
  // load, and on resize), not at the moment of expanding: measuring on
  // expand meant the line first rendered at a placeholder width and then
  // visibly jumped to the real one.
  const displayedKey = displayedEvents.map(e => e.id).join(',');
  useEffect(() => {
    const measure = () => {
      const next: Record<number, string> = {};
      Object.entries(titleRefs.current).forEach(([id, el]) => {
        // Runs on past the title text to the tip of the expand arrow
        // (its gap + 18px open width, see .expand-arrow::after).
        if (el) {
          const gap = el.parentElement ? parseFloat(getComputedStyle(el.parentElement, '::after').marginLeft) || 0 : 0;
          next[Number(id)] = `${el.offsetWidth + gap + 18}px`;
        }
      });
      setSignatureWidths(next);
    };
    measure();
    document.fonts?.ready.then(measure).catch(() => {});
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [displayedKey, language]);

  // Reused across the icon and the signature accent line so both always
  // agree.
  const getEventColor = (type: EventType) => {
    switch (type) {
      case 'work': return 'var(--color-alt-accent)';
      case 'education': return 'var(--color-main-accent)';
      case 'other': return 'var(--color-deep-red)';
    }
  };

  const getEventIcon = (type: EventType) => {
    switch (type) {
      case 'work': return <Briefcase size={17} strokeWidth={1.75} style={{color: getEventColor('work')}} />;
      case 'education': return <GraduationCap size={17} strokeWidth={1.75} style={{color: getEventColor('education')}} />;
      case 'other': return <Award size={17} strokeWidth={1.75} style={{color: getEventColor('other')}} />;
    }
  };



  return (
    <section className="bg-[var(--color-bg-primary)] min-h-screen transition-colors duration-300" style={{fontFamily: 'Short Stack, cursive'}}>
      <Helmet>
        <title>{t('seo.experience.title')}</title>
        <meta name="description" content={t('seo.experience.description')} />
        <meta property="og:title" content={t('seo.experience.title')} />
        <meta property="og:description" content={t('seo.experience.description')} />
        <meta property="og:image" content="https://pfjacobs.com/images/profile_without_bg.png" />
        <meta property="og:url" content="https://pfjacobs.com/experience" />
      </Helmet>
      <div className="container mx-auto px-6 py-[8rem] max-w-6xl">
        <h2 className="text-3xl md:text-4xl lg:text-5xl text-center text-[var(--color-text-primary)] mb-16 font-normal" style={{fontFamily: 'Indie Flower, cursive'}}><T k="experiencePage.title" /></h2>

        <div>
          <div>

        {/* Filter pills: FilterCheck, styled in input.css (.filter-check). */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mb-8">
          <FilterCheck label={t('experiencePage.types.work')} color={getEventColor('work')} checked={selectedFilters.includes('work')} onClick={() => toggleFilter('work')} />
          <FilterCheck label={t('experiencePage.types.education')} color={getEventColor('education')} checked={selectedFilters.includes('education')} onClick={() => toggleFilter('education')} />
          <FilterCheck label={t('experiencePage.types.other')} color={getEventColor('other')} checked={selectedFilters.includes('other')} onClick={() => toggleFilter('other')} />
        </div>

        {/* Margin-note layout (picked from a round of /timeline-lab
            comparisons, now removed): dates pulled into their own column
            — a notebook date margin — with entries in a single column
            beside them, no spine/dot/connector at all. Stacks to a single
            column on mobile (date as a small line above the entry)
            instead of the fixed-width side-by-side split, which would
            otherwise squeeze the entry column too narrow on a phone. */}
        <div>
          {displayedEvents.map((event, index) => (
            <TimelineRow
              key={event.id}
              data={{
                date: event.date,
                title: event.title,
                where: event.where,
                url: event.url,
                description: event.description,
                icon: getEventIcon(event.type),
                color: getEventColor(event.type),
              }}
              isOpen={expandedEvents.includes(event.id)}
              onToggle={() => toggleEvent(event.id)}
              titleRef={(el) => { titleRefs.current[event.id] = el; }}
              signatureWidth={signatureWidths[event.id]}
              className={`transition-all duration-700 ease-in-out transform ${
                showAllEvents || index < 4
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: showAllEvents ? `${index * 100}ms` : '0ms' }}
            />
          ))}
        </div>

        {/* Download CV as a handwritten margin note (picked in /cv-lab):
            beside Show more, tilted, with a small drawn arrow aiming back
            up-left at the timeline it summarises. On phones it drops
            under the button instead of hanging off to its side. */}
        <div className="relative flex flex-col items-center mt-8">
          {filteredEvents.length > 5 && (
            <button
              onClick={() => setShowAllEvents(!showAllEvents)}
              className="font-bold py-2 px-4 rounded-md transition-all duration-300"
              style={{backgroundColor: 'var(--color-btn-primary)', color: 'var(--color-btn-text)'}}
            >
              {showAllEvents ? <T k="experiencePage.showLess" /> : <T k="experiencePage.showMore" />}
            </button>
          )}
          <a
            href="/documents/cv.pdf"
            download="cv_pieter_jacobs.pdf"
            className={`inline-block whitespace-nowrap text-xl text-[var(--color-alt-accent)] hover:underline ${filteredEvents.length > 5 ? 'mt-3 sm:mt-1 sm:absolute sm:left-1/2 sm:ml-16 sm:top-full' : ''}`}
            style={{ fontFamily: 'Indie Flower, cursive', transform: 'rotate(-5deg)' }}
          >
            <svg width="28" height="18" viewBox="0 0 28 18" className="inline align-middle mx-1" aria-hidden="true" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round">
              <path d="M24 16 Q12 14 6 3" />
              <path d="M5 9 L6 3 L12 4" />
            </svg>
            {filteredEvents.length > 5 ? <T k="experiencePage.downloadCv" /> : <T k="experiencePage.downloadCvAlone" />}
          </a>
        </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Timeline;
